import { useCallback, useEffect, useId, useRef, useState } from 'react'

export interface Column {
  /** ISO start of the bucket this column covers. */
  start: string
  /** Stacked bottom-to-top, in this order. */
  parts: number[]
  /** The bucket is still filling: drawn faded, and labelled "so far". */
  partial?: boolean
}
export interface Series { label: string; color: string }

const HEIGHT = 96
// A column never fills its band: the leftover is air, and the surface gap between
// neighbours falls out of it. Capped in pixels too, so three buckets are three
// columns rather than three slabs — which is why the chart measures itself and
// uses its own pixel width as the viewBox, one unit to one pixel.
const FILL = 0.5
const MAX_BAR = 24
// At most this many x labels, so they never collide: first, last and a couple between.
const MAX_TICKS = 4

/** The axis top: the next round number at or above the peak, so the label means
 *  something and the columns are read against it rather than against each other. */
function niceMax(peak: number) {
  if (peak <= 0) return 0
  const magnitude = 10 ** Math.floor(Math.log10(peak))
  // A close ladder: a peak of 0.51 should top out at 0.6, not waste half the plot
  // climbing to 1. Every step still reads as a round number on the axis.
  for (const step of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (peak <= step * magnitude) return step * magnitude
  }
  return 10 * magnitude
}

/** Evenly spaced column indices to label, always including the first and the last. */
function tickIndices(count: number) {
  if (count <= MAX_TICKS) return [...Array(count).keys()]
  const step = (count - 1) / (MAX_TICKS - 1)
  return [...new Set([...Array(MAX_TICKS).keys()].map(index => Math.round(index * step)))]
}
const RADIUS = 4     // rounded data-end, square at the baseline
// Stacked segments are separated by surface, not by a stroke. The chart is rendered
// at a fixed 96px height, so one viewBox unit is one pixel vertically.
const STACK_GAP = 2
// A part worth a fraction of a pixel still happened, and a stacked chart that drops
// it is claiming a series it then never draws — the legend says Model, the columns
// are all EC2. Anything non-zero gets at least this, and the gap comes out of what
// is left above it rather than out of the part itself.
const MIN_PART = 3

/** A column's path: rounded at the top, square where it meets the baseline. */
function column(x: number, y: number, width: number, height: number, round: boolean) {
  const r = round ? Math.min(RADIUS, width / 2, height) : 0
  return `M${x},${y + height}V${y + r}a${r},${r} 0 0 1 ${r},${-r}h${width - 2 * r}a${r},${r} 0 0 1 ${r},${r}V${y + height}Z`
}

/**
 * One binned measure over time. Single series or a small stack; never two scales.
 * Bars are capped so a wide chart is air rather than slabs, the top of each column
 * is rounded and its foot is square, and a 2px surface gap does the separating.
 */
export function ColumnChart({ columns, series, format, caption, tick, emptyLabel, compact = false }: {
  columns: Column[]
  series: Series[]
  format: (value: number) => string
  /** Describes one bucket, e.g. "3 min". Used by the tooltip and the summary. */
  caption: (start: string) => string
  /** Short axis label for a bucket, e.g. "16:05" or "Sep 18". */
  tick: (start: string) => string
  emptyLabel: string
  /** Small inline trend, retaining the same data and hover details. */
  compact?: boolean
}) {
  const [hover, setHover] = useState<number>()
  const tooltipId = useId()
  const [plot, setPlot] = useState(0)
  // The plot measures itself, and it has to start measuring the moment its element
  // exists rather than on the component's first render: a chart whose window is
  // empty renders a sentence instead of a plot, so a mount effect found no element,
  // observed nothing, and left the width at 0 — after which switching to a window
  // that did have data drew an axis, a caption and no columns at all.
  const observer = useRef<ResizeObserver>()
  const box = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect()
    if (!node) return
    observer.current = new ResizeObserver(([entry]) => setPlot(entry.contentRect.width))
    observer.current.observe(node)
    setPlot(node.clientWidth)
  }, [])
  useEffect(() => () => observer.current?.disconnect(), [])
  const totals = columns.map(item => item.parts.reduce((sum, value) => sum + value, 0))
  const peak = Math.max(...totals, 0)
  const axis = niceMax(peak)
  const ticks = new Set(compact ? [0, columns.length - 1] : tickIndices(columns.length))
  const band = plot / Math.max(1, columns.length)
  const height = compact ? 56 : HEIGHT
  const width = Math.min(band * FILL, compact ? 14 : MAX_BAR)
  const inset = (band - width) / 2
  const active = hover != null ? columns[hover] : undefined
  const tooltipWidth = Math.min(220, plot)
  // Anchor to the bar, keeping the tooltip inside the plot even at either edge.
  const tooltipLeft = Math.min(plot - tooltipWidth, Math.max(0, ((hover ?? 0) + .5) * band - tooltipWidth / 2))
  const tooltipTop = Math.max(0, height - ((totals[hover ?? 0] ?? 0) / axis) * height) - 10

  if (peak <= 0) return <p className={`chart-empty${compact ? ' compact' : ''}`}>{emptyLabel}</p>

  return <div className={`column-chart${compact ? ' compact' : ''}`}>
    <div className="chart-plot">
      <div className="y-axis" aria-hidden="true"><span>{format(axis)}</span><span>0</span></div>
      <div className="chart-area" ref={box}>
    {plot > 0 && <svg viewBox={`0 0 ${plot} ${height}`} preserveAspectRatio="none" role="img"
         aria-label={`${series.map(item => item.label).join(' and ')}, ${columns.length} buckets, peak ${format(peak)}`}>
      {columns.map((item, index) => {
        let y = height
        return <g key={item.start} tabIndex={0}
          aria-label={`${caption(item.start)}${item.partial ? ' (so far)' : ''}: ${series.map((entry, part) => `${entry.label} ${format(item.parts[part] ?? 0)}`).join(', ')}`}
          aria-describedby={hover === index ? tooltipId : undefined}
          onPointerEnter={() => setHover(index)} onPointerLeave={() => setHover(undefined)}
          onFocus={() => setHover(index)} onBlur={() => setHover(undefined)}
          onKeyDown={event => { if (event.key === 'Escape') setHover(undefined) }}>
          <rect x={index * band} y="0" width={band} height={height}
                className={`chart-hit${hover === index ? ' on' : ''}`} />
          {item.parts.map((value, part) => {
            if (value <= 0) return null
            const barHeight = Math.max(axis ? (value / axis) * height : 0, MIN_PART)
            y -= barHeight
            // The top-most non-empty part carries the rounded end.
            const top = item.parts.slice(part + 1).every(rest => rest <= 0)
            const gap = top ? 0 : Math.min(STACK_GAP, Math.max(0, barHeight - MIN_PART))
            return <path key={part} d={column(index * band + inset, y + gap, width, barHeight - gap, top)}
                         fill={series[part]?.color} className={item.partial ? 'chart-partial' : undefined} />
          })}
        </g>
      })}
    </svg>}
        {active && <div className="chart-tooltip" id={tooltipId} role="tooltip"
          style={{ left: tooltipLeft, top: tooltipTop, width: tooltipWidth }}>
          <span className="chart-tooltip-time">{caption(active.start)}{active.partial && ' · so far'}</span>
          <strong>{format(totals[hover!])}{series.length === 1 && <small> {series[0].label.toLowerCase()}</small>}</strong>
          {series.length > 1 && <dl>{series.map((item, part) => <div key={item.label}>
            <dt><i style={{ background: item.color }} />{item.label}</dt><dd>{format(active.parts[part] ?? 0)}</dd>
          </div>)}</dl>}
        </div>}
        {/* Labels are placed at their column's centre rather than inside it: a
            24-column chart gives each column ~19px, which clips a timestamp. */}
        <div className="x-axis" aria-hidden="true">{[...ticks].map(index => {
          const last = index === columns.length - 1
          const centre = ((index + 0.5) / columns.length) * 100
          return <span className="x-tick" key={index} style={index === 0
            ? { left: 0 }
            : last ? { right: 0 } : { left: `${centre}%`, transform: 'translateX(-50%)' }}>
            {tick(columns[index].start)}
          </span>
        })}</div>
      </div>
    </div>
  </div>
}
